import {
	AllPatientPrograms,
	DatedPatientProgram,
	DraftProgram,
	EndedProgram,
	ExtendedPatientProgram,
	PatientProgram,
	PatientProgramEditor,
} from "@domain/program/patient-program";
import { PatientProgramsService } from "@domain/program/patient-programs-service";
import { ProgramMetadata } from "@domain/program/program";
import { ProgramStatus } from "@domain/program/program-status/program-status";
import { isNotNil } from "@utils/assert";
import { DateRange, dateRangesOverlap, datesAreOnSameDay, now } from "@utils/date-utils";
import { Dayjs } from "dayjs";
import { Map } from "immutable";
import _ from "lodash";
import { observable } from "micro-observables";

export class PatientProgramsManager {
	private readonly _allPatientsPrograms = observable<Map<string, ExtendedPatientProgram>>(Map());
	allPatientsPrograms = this._allPatientsPrograms.select(item => Array.from(item.values()));

	private readonly _allProgramsByPatient = observable<Map<string, AllPatientPrograms>>(Map());
	allProgramsByPatient = this._allProgramsByPatient.readOnly();

	constructor(private readonly patientProgramsService: PatientProgramsService) {}

	async fetchAllPatientsPrograms(): Promise<void> {
		const patientsPrograms = await this.patientProgramsService.loadAllPatientsPrograms();
		this._allPatientsPrograms.set(
			Map<string, ExtendedPatientProgram>(patientsPrograms.map((item: ExtendedPatientProgram) => [item.id, item])),
		);
	}

	async fetchAllPatientPrograms(patientId: string): Promise<void> {
		const programs = await this.patientProgramsService.loadAllPatientPrograms(patientId);
		this._allProgramsByPatient.update(map => map.set(patientId, programs));
	}

	getOverlappingProgram(
		patientId: string,
		startDate: Dayjs,
		endDate: Dayjs,
		programId?: string,
	): OverlapPatientProgram | null {
		const newProgramRange = [startDate, endDate] as DateRange;

		if (endDate.isBefore(startDate)) {
			return null;
		}

		const patientPrograms = this.allProgramsByPatient.get().get(patientId);
		if (patientPrograms) {
			const filteredPrograms = [
				...(patientPrograms.ongoingProgram ? [patientPrograms.ongoingProgram] : []),
				...patientPrograms.scheduledPrograms,
				...patientPrograms.endedPrograms,
			].filter(program => program.id !== programId);

			for (const program of filteredPrograms) {
				const existingProgramRange = [program.startDate, program.endDate] as DateRange;
				if (dateRangesOverlap(newProgramRange, existingProgramRange)) return program;
			}
		}

		return null;
	}

	stopProgramLocally(patientId: string, programMetadata: ProgramMetadata, editor: PatientProgramEditor): void {
		const stoppedProgram = this.getStoppedProgram(patientId, programMetadata, editor);

		if (!stoppedProgram) return;

		const patientPrograms = this.allProgramsByPatient.get().get(patientId);
		if (patientPrograms?.ongoingProgram?.id === programMetadata.id) {
			if (stoppedProgram.status === ProgramStatus.Ended) {
				const modifiedPatientPrograms = {
					scheduledPrograms: patientPrograms.scheduledPrograms,
					draftPrograms: patientPrograms.draftPrograms,
					endedPrograms: [stoppedProgram, ...patientPrograms.endedPrograms],
				};
				this._allProgramsByPatient.update(map => map.set(patientId, modifiedPatientPrograms));
			} else {
				const modifiedPatientPrograms = {
					scheduledPrograms: patientPrograms.scheduledPrograms,
					draftPrograms: [stoppedProgram, ...patientPrograms.draftPrograms],
					endedPrograms: patientPrograms.endedPrograms,
				};
				this._allProgramsByPatient.update(map => map.set(patientId, modifiedPatientPrograms));
			}
		}

		this.updatePatientsProgramsLocaly(stoppedProgram.id, stoppedProgram);
	}

	startProgramLocally(patientId: string, startedPatientProgram: DatedPatientProgram): void {
		const today = now();
		const isOngoingProgram =
			datesAreOnSameDay(today, startedPatientProgram.startDate) || startedPatientProgram.startDate.isBefore(today);

		const patientPrograms = this.allProgramsByPatient.get().get(patientId);
		if (patientPrograms) {
			const filteredPrograms = this.getFilteredPrograms(patientPrograms, startedPatientProgram.id);
			const newAllpatientPrograms: AllPatientPrograms = isOngoingProgram
				? {
						...filteredPrograms,
						ongoingProgram: { ...startedPatientProgram, status: ProgramStatus.Ongoing },
					}
				: {
						...filteredPrograms,
						scheduledPrograms: [
							{ ...startedPatientProgram, status: ProgramStatus.Scheduled as const },
							...filteredPrograms.scheduledPrograms,
						].sort((p1, p2) => p1.startDate.diff(p2.startDate)),
					};
			this._allProgramsByPatient.update(map => map.set(patientId, newAllpatientPrograms));
		}

		this.updatePatientsProgramsLocaly(
			startedPatientProgram.id,
			isOngoingProgram
				? { ...startedPatientProgram, status: ProgramStatus.Ongoing }
				: { ...startedPatientProgram, status: ProgramStatus.Scheduled as const },
		);
	}

	deleteProgramLocally(patientId: string, deletedId: string): void {
		const patientPrograms = this.allProgramsByPatient.get().get(patientId);
		if (patientPrograms) {
			const modifiedPatientPrograms = this.getFilteredPrograms(patientPrograms, deletedId);
			this._allProgramsByPatient.update(map => map.set(patientId, modifiedPatientPrograms));
		}

		const allPatientsPrograms = this._allPatientsPrograms.get();
		const programToDelete = allPatientsPrograms.get(deletedId);
		if (programToDelete) {
			this._allPatientsPrograms.set(allPatientsPrograms.delete(deletedId));
		}
	}

	async duplicateProgramAsDraft(patientId: string, programId: string): Promise<string> {
		const duplicatedProgram = await this.patientProgramsService.duplicateProgramAsDraft(patientId, programId);

		const patientPrograms = this.allProgramsByPatient.get().get(patientId);
		if (patientPrograms) {
			const modifiedPatientPrograms = {
				...patientPrograms,
				draftPrograms: [duplicatedProgram, ...patientPrograms.draftPrograms],
			};
			this._allProgramsByPatient.update(map => map.set(patientId, modifiedPatientPrograms));
		}

		this.updatePatientsProgramsLocaly(programId, duplicatedProgram);

		return duplicatedProgram.id;
	}

	async reset(): Promise<void> {
		this._allProgramsByPatient.set(Map());
		this._allPatientsPrograms.set(Map());
	}

	private getFilteredPrograms(patientPrograms: AllPatientPrograms, programIdToFilter: string): AllPatientPrograms {
		return {
			ongoingProgram:
				patientPrograms.ongoingProgram?.id === programIdToFilter ? undefined : patientPrograms.ongoingProgram,
			scheduledPrograms: patientPrograms.scheduledPrograms.filter(program => program.id !== programIdToFilter),
			draftPrograms: patientPrograms.draftPrograms.filter(program => program.id !== programIdToFilter),
			endedPrograms: patientPrograms.endedPrograms.filter(program => program.id !== programIdToFilter),
		};
	}

	private readonly getStoppedProgram = (
		patientId: string,
		programMetadata: ProgramMetadata,
		editor: PatientProgramEditor,
	): EndedProgram | DraftProgram | undefined => {
		const programToStop =
			this.allProgramsByPatient.get().get(patientId)?.ongoingProgram ??
			this._allPatientsPrograms.get().get(programMetadata.id);

		if (programToStop?.status !== ProgramStatus.Ongoing) return;

		if (programMetadata.status === ProgramStatus.Ended && isNotNil(programMetadata.endDate)) {
			return {
				...programToStop,
				endDate: programMetadata.endDate,
				stopped: true,
				status: programMetadata.status,
				lastEditionMetadata: {
					date: now(),
					editor,
				},
			} as EndedProgram;
		} else if (programMetadata.status === ProgramStatus.Draft) {
			return {
				..._.omit(programToStop, "startDate", "endDate"),
				status: ProgramStatus.Draft,
				lastEditionMetadata: {
					date: now(),
					editor,
				},
			} as DraftProgram;
		}
	};

	private readonly updatePatientsProgramsLocaly = (programId: string, program: PatientProgram) => {
		const allPatientsPrograms = this._allPatientsPrograms.get();
		const programToUpdate = allPatientsPrograms.get(programId);
		if (programToUpdate) {
			this._allPatientsPrograms.set(
				allPatientsPrograms.set(program.id, {
					...program,
					patient: programToUpdate.patient,
				}),
			);
		}
	};
}

type OverlapPatientProgram = DatedPatientProgram;
